// État du système (administrateur) : sauvegardes, disque, test de restauration, copie hors site, site distant, alertes.
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { CheckCircleOutline, ErrorOutline, HelpOutline, NotificationsActiveOutlined, RefreshOutlined } from '@mui/icons-material';
import Layout from '../components/Layout';
import { Panel } from '../components/ui';
import { systemeAPI } from '../services/api';
import { apiError, dateHeureFr } from '../utils/format';

const Icone = ({ ok }) => (ok === true ? <CheckCircleOutline sx={{ color: 'success.main' }} /> : ok === false ? <ErrorOutline sx={{ color: 'error.main' }} /> : <HelpOutline sx={{ color: 'text.disabled' }} />);

const Systeme = () => {
  const [d, setD] = useState(null);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const charger = useCallback(() => systemeAPI.etat().then((r) => setD(r.data)).catch((e) => setError(apiError(e))), []);
  useEffect(() => { charger(); }, [charger]);
  const act = async (fn) => { setBusy(true); setError(''); try { const r = await fn(); setInfo(r.data.message); charger(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); } };
  const ko = d?.controles.filter((c) => c.ok === false) || [];
  const s = d?.sauvegarde;

  return (
    <Layout title="État du système" plain
      actions={<>
        <Button startIcon={<NotificationsActiveOutlined />} disabled={busy} onClick={() => act(systemeAPI.testAlerte)}>Tester les alertes</Button>
        <Button variant="contained" startIcon={<RefreshOutlined />} disabled={busy} onClick={() => act(systemeAPI.verifier)}>Vérifier maintenant</Button>
      </>}>
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {!d && !error && <LinearProgress />}
      {d && (
        <>
          <Alert severity={ko.length ? 'error' : 'success'} sx={{ mb: 2 }}>
            {ko.length ? `${ko.length} contrôle(s) en défaut : les administrateurs ont été prévenus (cloche et email), avec un rappel chaque jour jusqu'au retour à la normale.`
              : 'Tout va bien. Les contrôles tournent toutes les 10 minutes ; en cas de problème, les administrateurs sont prévenus par la cloche et par email.'}
          </Alert>
          <Panel flush>
            <Table>
              <TableBody>
                {d.controles.map((c) => (
                  <TableRow key={c.cle}>
                    <TableCell sx={{ width: 40 }}><Icone ok={c.ok} /></TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 260 }}>{c.libelle}</TableCell>
                    <TableCell>{c.message}
                      {c.depuis && c.ok === false && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>en défaut depuis le {dateHeureFr(c.depuis)}{c.alerte_le ? ` · dernière alerte ${dateHeureFr(c.alerte_le)}` : ''}</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
            <Panel title="Sauvegardes">
              {s ? (
                <Typography sx={{ fontSize: 14 }}>
                  Chaque jour à <b>{s.heure ?? '?'} h</b>, conservées <b>{s.conservation_jours ?? '?'} jours</b> ({s.nombre ?? '?'} fichier(s), {s.total_sauvegardes ? `${Math.round(s.total_sauvegardes / 1024)} Mo` : '—'}) dans le dossier <code>backups</code> du serveur.
                  Test de restauration automatique le 1er de chaque mois.
                </Typography>
              ) : <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Le conteneur de sauvegarde n'a encore rien signalé.</Typography>}
            </Panel>
            <Panel title="Copie hors site et surveillance croisée">
              <Typography sx={{ fontSize: 14, mb: 1 }}>
                Copie de la base de l'autre serveur : {d.config.copie_distante_url ? <Chip size="small" color="success" label={d.config.copie_distante_url} /> : <Chip size="small" label="non configurée" />}
              </Typography>
              <Typography sx={{ fontSize: 14, mb: 1 }}>
                Site surveillé : {d.config.surveiller_url ? <Chip size="small" color="success" label={d.config.surveiller_url} /> : <Chip size="small" label="aucun" />}
              </Typography>
              <Typography sx={{ fontSize: 14, mb: 1 }}>
                Export vers l'autre serveur : {d.config.export_actif ? <Chip size="small" color="success" label="autorisé (clé définie)" /> : <Chip size="small" label="désactivé" />}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                Réglage dans le fichier .env du serveur : sur le VPS, SAUVEGARDE_CLE (créée à l'installation) ; sur le serveur de l'usine, COPIE_DISTANTE_URL, COPIE_DISTANTE_CLE et SURVEILLER_URL
                (demandés par installer-ubuntu.sh). Voir le README, section « Sauvegardes et surveillance ».
              </Typography>
            </Panel>
          </Box>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>Serveur démarré le {dateHeureFr(d.serveur.demarre_le)} · Node {d.serveur.node}</Typography>
        </>
      )}
    </Layout>
  );
};

export default Systeme;
